import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Paper,
  FormHelperText
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import discountService from '../services/discountService';
import { productService } from '../services/productService';
import LoadingSpinner from '../components/LoadingSpinner';

const DiscountForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [formData, setFormData] = useState({
    type: 'flat',
    discountMode: 'percentage',
    value: '',
    startDate: new Date(),
    endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    products: [],
    isGlobal: false,
    isFirstOrderOnly: false,
    promoCode: '',
    description: '',
    minOrderAmount: 0,
    maxDiscountAmount: '',
    usageLimit: '',
    isActive: true,
    buyProductId: '',
    requiredQty: 1,
    freeProductId: '',
    freeQty: 1
  });

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const response = await productService.getAllProducts();
        console.log('Products response:', response);
        // The API returns { products: [...], pagination: {...} }
        const products = response.products || response.data?.products || [];
        console.log('Products extracted:', products);
        setProducts(products);
      } catch (error) {
        console.error('Error fetching products:', error);
        showSnackbar('Error fetching products', 'error');
      }
    };

    const loadDiscount = async () => {
      try {
        setLoading(true);
        const response = await discountService.getDiscountById(id);
        if (response.success) {
          const discount = response.data;
          setFormData({
            type: discount.type,
            discountMode: discount.discountMode,
            value: discount.value,
            startDate: new Date(discount.startDate),
            endDate: new Date(discount.endDate),
            products: discount.products?.map(p => p._id) || [],
            isGlobal: !discount.products || discount.products.length === 0,
            isFirstOrderOnly: !!discount.isFirstOrderOnly,
            promoCode: discount.promoCode || '',
            description: discount.description || '',
            minOrderAmount: discount.minOrderAmount || 0,
            maxDiscountAmount: discount.maxDiscountAmount || '',
            usageLimit: discount.usageLimit || '',
            isActive: discount.isActive,
            buyProductId: discount.buyProductId || '',
            requiredQty: discount.requiredQty || 1,
            freeProductId: discount.freeProductId || '',
            freeQty: discount.freeQty || 1
          });
        }
      } catch (error) {
        console.error('Error fetching discount:', error);
        showSnackbar('Error fetching discount', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
    if (isEditMode) {
      loadDiscount();
    }
  }, [id, isEditMode]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };

      if (field === 'type') {
        if (value !== 'coupon') {
          updated.isFirstOrderOnly = false;
        }
        if (value === 'buy_x_get_y') {
          updated.discountMode = 'amount';
          updated.value = 0;
          updated.minOrderAmount = 0;
          updated.maxDiscountAmount = '';
          updated.usageLimit = '';
        }
      }

      return updated;
    });
    
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    // Value validation (skip for buy_x_get_y)
    if (formData.type !== 'buy_x_get_y') {
      if (!formData.value || formData.value <= 0) {
        errors.value = 'Value must be greater than 0';
      }

      if (formData.discountMode === 'percentage' && formData.value > 100) {
        errors.value = 'Percentage value cannot exceed 100';
      }
    }

    // Date validation
    if (!formData.startDate) {
      errors.startDate = 'Start date is required';
    }

    // Buy X Get Y validation
    if (formData.type === 'buy_x_get_y') {
      if (!formData.buyProductId) {
        errors.buyProductId = 'Select the product customers must buy';
      }
      if (!formData.freeProductId) {
        errors.freeProductId = 'Select the free product to award';
      }
      if (!formData.requiredQty || Number(formData.requiredQty) < 1) {
        errors.requiredQty = 'Required quantity must be at least 1';
      }
      if (!formData.freeQty || Number(formData.freeQty) < 1) {
        errors.freeQty = 'Free quantity must be at least 1';
      }
    }

    if (!formData.endDate) {
      errors.endDate = 'End date is required';
    }

    if (formData.startDate && formData.endDate && formData.startDate >= formData.endDate) {
      errors.endDate = 'End date must be after start date';
    }

    // Coupon-specific validation
    if (formData.type === 'coupon') {
      if (!formData.promoCode || formData.promoCode.trim() === '') {
        errors.promoCode = 'Promo code is required for coupon type';
      }
      
      if (formData.promoCode && formData.promoCode.length < 3) {
        errors.promoCode = 'Promo code must be at least 3 characters';
      }
      
      if (formData.promoCode && formData.promoCode.length > 20) {
        errors.promoCode = 'Promo code must not exceed 20 characters';
      }
    }

    // Flat discount validation - products required unless global
    if (formData.type === 'flat' && !formData.isGlobal) {
      if (!formData.products || formData.products.length === 0) {
        errors.products = 'Select at least one product or enable "Apply to all products"';
      }
    }

    // Min order amount validation
    if (formData.minOrderAmount && formData.minOrderAmount < 0) {
      errors.minOrderAmount = 'Minimum order amount cannot be negative';
    }

    // Max discount amount validation
    if (formData.maxDiscountAmount && formData.maxDiscountAmount < 0) {
      errors.maxDiscountAmount = 'Maximum discount amount cannot be negative';
    }

    if (formData.maxDiscountAmount && formData.discountMode === 'amount' && parseFloat(formData.maxDiscountAmount) < parseFloat(formData.value)) {
      errors.maxDiscountAmount = 'Maximum discount amount must be greater than or equal to discount value';
    }

    // Usage limit validation (only for coupons)
    if (formData.type === 'coupon' && formData.usageLimit) {
      if (formData.usageLimit < 1) {
        errors.usageLimit = 'Usage limit must be at least 1';
      }
      
      if (!Number.isInteger(parseFloat(formData.usageLimit))) {
        errors.usageLimit = 'Usage limit must be a whole number';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const discountData = {
        ...formData,
        value: formData.type === 'buy_x_get_y' ? 0 : parseFloat(formData.value),
        minOrderAmount: formData.type === 'buy_x_get_y' ? 0 : (parseFloat(formData.minOrderAmount) || 0),
        maxDiscountAmount: formData.type === 'buy_x_get_y'
          ? null
          : formData.maxDiscountAmount
            ? parseFloat(formData.maxDiscountAmount)
            : null,
        // If global, send empty products array; otherwise send selected products
        products: formData.isGlobal ? [] : formData.products
      };

      discountData.isFirstOrderOnly = formData.type === 'coupon' ? formData.isFirstOrderOnly : false;

      // Only add promo code and usage limit for coupon discounts
      if (formData.type === 'coupon') {
        discountData.promoCode = formData.promoCode ? formData.promoCode.trim().toUpperCase() : null;
        discountData.usageLimit = formData.usageLimit ? parseInt(formData.usageLimit) : null;
      } else {
        // For non-coupon discounts, explicitly set these to null/undefined
        discountData.promoCode = null;
        discountData.usageLimit = null;
      }

      if (formData.type !== 'buy_x_get_y') {
        discountData.buyProductId = null;
        discountData.requiredQty = null;
        discountData.freeProductId = null;
        discountData.freeQty = null;
      } else {
        discountData.buyProductId = formData.buyProductId;
        discountData.requiredQty = parseInt(formData.requiredQty, 10);
        discountData.freeProductId = formData.freeProductId;
        discountData.freeQty = parseInt(formData.freeQty, 10);
      }

      if (isEditMode) {
        await discountService.updateDiscount(id, discountData);
        showSnackbar('Discount updated successfully');
      } else {
        await discountService.createDiscount(discountData);
        showSnackbar('Discount created successfully');
      }

      navigate('/discounts');
    } catch (error) {
      console.error('Error saving discount:', error);
      showSnackbar(error.response?.data?.message || 'Error saving discount', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/discounts');
  };

  if (loading && isEditMode) {
    return <LoadingSpinner />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            {isEditMode ? 'Edit Discount' : 'Add New Discount'}
          </Typography>
        </Box>

        <Paper sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!formErrors.type}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  label="Type"
                >
                  <MenuItem value="flat">Flat Discount</MenuItem>
                  <MenuItem value="coupon">Coupon Discount</MenuItem>
                  <MenuItem value="buy_x_get_y">Buy X Get Y Free</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Discount Mode</InputLabel>
                <Select
                  value={formData.discountMode}
                  onChange={(e) => handleInputChange('discountMode', e.target.value)}
                  label="Discount Mode"
                  disabled={formData.type === 'buy_x_get_y'}
                >
                  <MenuItem value="percentage">Percentage</MenuItem>
                  <MenuItem value="amount">Amount</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={`Value ${formData.discountMode === 'percentage' ? '(%)' : '(₹)'}`}
                type="number"
                value={formData.value}
                onChange={(e) => handleInputChange('value', e.target.value)}
                error={!!formErrors.value}
                helperText={
                  formData.type === 'buy_x_get_y'
                    ? 'Value not required for Buy X Get Y discounts'
                    : formErrors.value
                }
                disabled={formData.type === 'buy_x_get_y'}
              />
            </Grid>

            {formData.type === 'coupon' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Promo Code"
                    value={formData.promoCode}
                    onChange={(e) => handleInputChange('promoCode', e.target.value.toUpperCase())}
                    error={!!formErrors.promoCode}
                    helperText={formErrors.promoCode}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isFirstOrderOnly}
                        onChange={(e) => handleInputChange('isFirstOrderOnly', e.target.checked)}
                      />
                    }
                    label="First Signup Offer (restrict to first order only)"
                  />
                  <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                    Enable to allow this coupon only for customers placing their first order.
                  </Typography>
                </Grid>
              </>
            )}

            {formData.type === 'buy_x_get_y' && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!formErrors.buyProductId}>
                    <InputLabel>Product to Buy</InputLabel>
                    <Select
                      value={formData.buyProductId}
                      onChange={(e) => handleInputChange('buyProductId', e.target.value)}
                      label="Product to Buy"
                    >
                      {products.map((product) => (
                        <MenuItem key={product._id} value={product._id}>
                          {product.title}
                        </MenuItem>
                      ))}
                    </Select>
                    {formErrors.buyProductId && <FormHelperText>{formErrors.buyProductId}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Required Quantity"
                    type="number"
                    value={formData.requiredQty}
                    onChange={(e) => handleInputChange('requiredQty', e.target.value)}
                    error={!!formErrors.requiredQty}
                    helperText={formErrors.requiredQty || 'Minimum quantity customer must buy'}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!formErrors.freeProductId}>
                    <InputLabel>Free Product</InputLabel>
                    <Select
                      value={formData.freeProductId}
                      onChange={(e) => handleInputChange('freeProductId', e.target.value)}
                      label="Free Product"
                    >
                      {products.map((product) => (
                        <MenuItem key={product._id} value={product._id}>
                          {product.title}
                        </MenuItem>
                      ))}
                    </Select>
                    {formErrors.freeProductId && <FormHelperText>{formErrors.freeProductId}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Free Quantity"
                    type="number"
                    value={formData.freeQty}
                    onChange={(e) => handleInputChange('freeQty', e.target.value)}
                    error={!!formErrors.freeQty}
                    helperText={formErrors.freeQty || 'Number of free items awarded per bundle'}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Start Date"
                value={formData.startDate}
                onChange={(date) => handleInputChange('startDate', date)}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    error={!!formErrors.startDate}
                    helperText={formErrors.startDate}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="End Date"
                value={formData.endDate}
                onChange={(date) => handleInputChange('endDate', date)}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    error={!!formErrors.endDate}
                    helperText={formErrors.endDate}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isGlobal}
                    onChange={(e) => handleInputChange('isGlobal', e.target.checked)}
                  />
                }
                label="Apply to all products (Global Discount)"
              />
              <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                When enabled, this discount will apply to all products regardless of selection below
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth error={!!formErrors.products} disabled={formData.isGlobal}>
                <InputLabel>Products</InputLabel>
                <Select
                  multiple
                  value={formData.products}
                  onChange={(e) => handleInputChange('products', e.target.value)}
                  label="Products"
                  renderValue={(selected) => `${selected.length} product(s) selected`}
                >
                  {products.map((product) => (
                    <MenuItem key={product._id} value={product._id}>
                      {product.title}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.products && <FormHelperText>{formErrors.products}</FormHelperText>}
              </FormControl>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                {formData.isGlobal 
                  ? 'Discount will apply to all products'
                  : formData.type === 'flat' 
                    ? 'Flat discounts must select at least one product or enable global'
                    : 'Select specific products or leave empty to apply to all'}
              </Typography>
            </Grid>

            {formData.type !== 'buy_x_get_y' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Minimum Order Amount"
                    type="number"
                    value={formData.minOrderAmount}
                    onChange={(e) => handleInputChange('minOrderAmount', e.target.value)}
                    error={!!formErrors.minOrderAmount}
                    helperText={formErrors.minOrderAmount}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Maximum Discount Amount"
                    type="number"
                    value={formData.maxDiscountAmount}
                    onChange={(e) => handleInputChange('maxDiscountAmount', e.target.value)}
                    error={!!formErrors.maxDiscountAmount}
                    helperText={formErrors.maxDiscountAmount}
                  />
                </Grid>
              </>
            )}

            {formData.type === 'coupon' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Usage Limit"
                  type="number"
                  value={formData.usageLimit}
                  onChange={(e) => handleInputChange('usageLimit', e.target.value)}
                  error={!!formErrors.usageLimit}
                  helperText={formErrors.usageLimit}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  />
                }
                label="Active"
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  onClick={handleCancel}
                  startIcon={<CancelIcon />}
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  startIcon={<SaveIcon />}
                  variant="contained"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          <Alert 
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
            severity={snackbar.severity}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default DiscountForm;
